import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChangelogPage: React.FC = () => {
  const [markdown, setMarkdown] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadChangelog = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch('/changelog.md', { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to load changelog (${response.status})`);
        }

        const content = await response.text();
        if (isMounted) {
          setMarkdown(content);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load changelog.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadChangelog();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Changelog
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Admin-only view of the project changelog maintained from the repository markdown file.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minHeight: 320 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box
              sx={{
                '& h1': { typography: 'h4', mt: 0, mb: 2.5 },
                '& h2': {
                  typography: 'h5',
                  mt: 4,
                  mb: 1.5,
                  pb: 0.75,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                },
                '& h3': { typography: 'h6', mt: 3, mb: 1.25 },
                '& p': { typography: 'body1', lineHeight: 1.8, mb: 1.5 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 0.75 },
                '& li > p': { mb: 0 },
                '& hr': { my: 4, border: 0, borderTop: theme => `1px solid ${theme.palette.divider}` },
                '& blockquote': {
                  m: 0,
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
                  bgcolor: 'grey.50',
                },
                '& code': {
                  px: 0.5,
                  py: 0.125,
                  borderRadius: 0.75,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  m: 0,
                  mb: 2,
                  p: 2,
                  overflowX: 'auto',
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                },
                '& pre code': {
                  p: 0,
                  bgcolor: 'transparent',
                  fontSize: 13,
                  lineHeight: 1.6,
                },
                '& a': {
                  color: 'primary.main',
                },
                '& table': {
                  width: '100%',
                  mb: 2,
                  borderCollapse: 'collapse',
                },
                '& th, & td': {
                  px: 1.5,
                  py: 1,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  textAlign: 'left',
                  verticalAlign: 'top',
                },
                '& th': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  hr: () => <Divider />,
                }}
              >
                {markdown}
              </ReactMarkdown>
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ChangelogPage;
